import React from 'react';

import { Text } from '../../../../../../components';
import { useConfigurator } from '../../../../store/configurator';
import type { Theme } from '../../../../lib/domain-types';
import { DESIGN, DESIGN_GRADIENTS } from '../../design.tokens';
import { NewBadge } from '../NewBadge';
import {
  Overlay,
  Header,
  BackButton,
  BackLabel,
  HeaderMeta,
  HeaderTitle,
  HeaderSub,
  Body,
  CurrentLabel,
  FilterPane,
  CurrentCard,
  CardOverlay,
  CardRow,
  CurrentLogo,
  LogoImg,
  CardMeta,
  NameRow,
  CurrentName,
  Subtitle,
  Results,
  ResultsCount,
  ResultsList,
  ResultCard,
  ResultIcon,
  ResultIconImg,
  ResultMeta,
  ResultName,
  ResultSubtitle,
  Footer,
  UseThemeButton,
} from './ThemesCatalog.styles';

const ArrowLeftRound = (): React.ReactElement => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="9.5" stroke={DESIGN.iconStroke} strokeWidth="1.4" />
    <path
      d="M13.5 8.5 10 12l3.5 3.5"
      stroke={DESIGN.iconStroke}
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = (): React.ReactElement => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path
      d="m5 10.5 3.5 3.5L15 7"
      stroke={DESIGN.checkStroke}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** The large highlighted card for the theme currently in focus. */
const CurrentThemeCard = ({ theme }: { theme: Theme }): React.ReactElement => (
  <CurrentCard>
    <CardOverlay $gradient={DESIGN_GRADIENTS.featuredOverlay} />
    <CardRow>
      <CurrentLogo>{theme.logo ? <LogoImg src={theme.logo} alt="" /> : null}</CurrentLogo>
      <CardMeta>
        <NameRow>
          <CurrentName text={theme.name} tag="paragraph" />
          {theme.isNew ? <NewBadge /> : null}
        </NameRow>
        <Subtitle text={theme.subtitle} tag="paragraph" />
      </CardMeta>
    </CardRow>
  </CurrentCard>
);

export type ThemesCatalogProps = {
  /** Close the catalog overlay. */
  onClose: () => void;
};

/**
 * Full-pane themes catalog overlay: the current theme and the remaining themes as
 * result cards. Picking a result applies the preview immediately (drives the live
 * device behind the panel) via `chooseTheme`.
 *
 * (The source app's "Filter & Refine" row was a static, non-functional placeholder
 * — fixed Category/Layout/Style values — so it's intentionally not rendered.)
 */
export const ThemesCatalog = ({ onClose }: ThemesCatalogProps): React.ReactElement | null => {
  const { themes, selection, chooseTheme } = useConfigurator();

  const current = themes.find((t) => t.id === selection.themeId) ?? themes[0];
  const results = themes.filter((t) => t.id !== current?.id);

  if (!current) return null;

  return (
    <Overlay>
      <Header>
        <BackButton type="button" data-ga-id="cfg-design-catalog-back" onClick={onClose}>
          <ArrowLeftRound />
          <BackLabel text="Back" tag="span" />
        </BackButton>
        <HeaderMeta>
          <HeaderTitle text="Themes catalog" tag="paragraph" />
          <HeaderSub text="Discover our full catalog of casino themes" tag="paragraph" />
        </HeaderMeta>
      </Header>

      <Body>
        <CurrentLabel text="Current Theme" tag="paragraph" />

        <CurrentThemeCard theme={current} />

        <FilterPane>
          <Results>
            <ResultsCount text="{{count}} results" placeholders={{ count: results.length }} tag="paragraph" />
            <ResultsList>
              {results.map((theme) => (
                <ResultCard key={theme.id} type="button" data-ga-id={`cfg-design-theme-${theme.id}`} onClick={() => chooseTheme(theme.id)}>
                  <CardOverlay $gradient={DESIGN_GRADIENTS.resultOverlay} />
                  <ResultIcon>{theme.logo ? <ResultIconImg src={theme.logo} alt="" /> : null}</ResultIcon>
                  <ResultMeta>
                    <NameRow>
                      <ResultName text={theme.name} tag="paragraph" />
                      {theme.isNew ? <NewBadge /> : null}
                    </NameRow>
                    <ResultSubtitle text={theme.subtitle} tag="paragraph" />
                  </ResultMeta>
                </ResultCard>
              ))}
            </ResultsList>
          </Results>
        </FilterPane>
      </Body>

      <Footer>
        <UseThemeButton type="button" data-ga-id="cfg-design-catalog-confirm" onClick={onClose}>
          <Text text="Use this theme" tag="span" />
          <CheckIcon />
        </UseThemeButton>
      </Footer>
    </Overlay>
  );
};

export default ThemesCatalog;
